import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { trainingMinuteService, employeeService, productService, machineService } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Plus, Search, Filter, Pencil, Trash2, ExternalLink, FileCheck, X } from 'lucide-react';

const STATUS_MAP = {
  pianificato: { label: 'Pianificato', cls: 'bg-blue-100 text-blue-700' },
  svolto: { label: 'Svolto', cls: 'bg-green-100 text-green-700' },
  archiviato: { label: 'Archiviato', cls: 'bg-slate-200 text-slate-600' },
};
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('it-IT') : '-';

const EMPTY_FORM = {
  training_date: '', start_time: '', end_time: '', duration_hours: '',
  topic: '', description: '', trainer: '', location: '',
  product_id: '', revision_id: '', machine_id: '', status: 'pianificato',
};

export default function VerbaliFormazionePage() {
  const { user } = useAuth();
  const canEdit = ['admin', 'quality', 'management'].includes(user?.role);
  const [minutes, setMinutes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({ status: '', trainer: '', product_id: '', machine_id: '' });
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);

  const [employees, setEmployees] = useState([]);
  const [products, setProducts] = useState([]);
  const [machines, setMachines] = useState([]);
  const [trainers, setTrainers] = useState([]);
  const [selectedEmployees, setSelectedEmployees] = useState([]);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (search) params.search = search;
      Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
      const res = await trainingMinuteService.getAll(params);
      setMinutes(res.data);
    } catch { toast.error('Errore caricamento verbali'); } finally { setLoading(false); }
  }, [search, filters]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    Promise.all([
      employeeService.getAll(),
      productService.getAll(),
      machineService.getAll(),
    ]).then(([e, p, m]) => {
      setEmployees(e.data);
      setProducts(p.data);
      setMachines(m.data);
      const t = [...new Set(e.data.map(emp => emp.role).filter(Boolean))];
      setTrainers(t);
    }).catch(e => { console.error('VerbaliFormazione: load lookups failed', e); });
  }, []);

  const calcDuration = (start, end) => {
    if (!start || !end) return '';
    const [sh, sm] = start.split(':').map(Number);
    const [eh, em] = end.split(':').map(Number);
    const diff = (eh * 60 + em - sh * 60 - sm) / 60;
    return diff > 0 ? diff.toFixed(1) : '';
  };

  const openCreate = () => {
    setForm(EMPTY_FORM);
    setSelectedEmployees([]);
    setShowModal(true);
  };

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    if (!form.training_date || !form.topic || !form.trainer || !form.duration_hours) {
      toast.error('Data, tema, formatore e durata obbligatori');
      return;
    }
    try {
      const participants = selectedEmployees.map(eid => ({
        employee_id: eid, presence: true, result: null, qualification: null, notes: null,
      }));
      const payload = {
        ...form,
        duration_hours: parseFloat(form.duration_hours),
        product_id: form.product_id || null,
        revision_id: form.revision_id || null,
        machine_id: form.machine_id || null,
        start_time: form.start_time || null,
        end_time: form.end_time || null,
        description: form.description || null,
        location: form.location || null,
        participants,
      };
      await trainingMinuteService.create(payload);
      toast.success('Verbale creato');
      setShowModal(false);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Errore'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Eliminare questo verbale?')) return;
    try { await trainingMinuteService.delete(id); toast.success('Verbale eliminato'); load(); } catch { toast.error('Errore'); }
  };

  const hasFilters = Object.values(filters).some(v => v);

  return (
    <div className="space-y-4" data-testid="verbali-page">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" data-testid="verbali-title">Verbali Formazione</h1>
          <p className="text-sm text-slate-500 mt-0.5">{minutes.length} verbali</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-2.5 text-slate-400" />
            <input type="text" placeholder="Cerca verbale..." value={search} onChange={e => setSearch(e.target.value)}
              className="pl-9 pr-3 py-2 text-sm border border-slate-200 rounded-lg w-64 focus:ring-2 focus:ring-blue-500 focus:outline-none" data-testid="verbale-search" />
          </div>
          <Button variant="outline" size="sm" onClick={() => setShowFilters(!showFilters)} data-testid="verbale-filters-btn"
            className={hasFilters ? 'border-blue-300 bg-blue-50 text-blue-700' : ''}>
            <Filter className="w-4 h-4 mr-1" />Filtri
          </Button>
          {canEdit && <Button size="sm" onClick={openCreate} data-testid="new-verbale-btn"><Plus className="w-4 h-4 mr-1" />Nuovo verbale</Button>}
        </div>
      </div>

      {showFilters && (
        <div className="bg-white border border-slate-200 rounded-xl p-4" data-testid="verbale-filters-panel">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">Stato</label>
              <select value={filters.status} onChange={e => setFilters(f => ({ ...f, status: e.target.value }))} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg" data-testid="filter-status">
                <option value="">Tutti</option>
                {Object.entries(STATUS_MAP).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">Formatore</label>
              <input type="text" value={filters.trainer} onChange={e => setFilters(f => ({ ...f, trainer: e.target.value }))} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg" placeholder="Cerca formatore..." data-testid="filter-trainer" />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">Prodotto</label>
              <select value={filters.product_id} onChange={e => setFilters(f => ({ ...f, product_id: e.target.value }))} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg" data-testid="filter-product">
                <option value="">Tutti</option>
                {products.map(p => <option key={p.id} value={p.id}>{p.code} - {p.name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">Macchina</label>
              <select value={filters.machine_id} onChange={e => setFilters(f => ({ ...f, machine_id: e.target.value }))} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg" data-testid="filter-machine">
                <option value="">Tutte</option>
                {machines.map(m => <option key={m.id} value={m.id}>{m.machine_code} - {m.machine_name}</option>)}
              </select>
            </div>
          </div>
          {hasFilters && <div className="mt-3 flex justify-end"><button onClick={() => setFilters({ status: '', trainer: '', product_id: '', machine_id: '' })} className="text-sm text-blue-600 hover:text-blue-800 font-medium">Resetta filtri</button></div>}
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="verbali-table">
        {loading ? <div className="p-8 text-center text-slate-400">Caricamento...</div> : minutes.length === 0 ? (
          <div className="p-8 text-center text-slate-400"><FileCheck className="w-12 h-12 mx-auto mb-3 text-slate-300" /><p className="font-medium text-slate-500">Nessun verbale trovato</p></div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="bg-slate-50 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">
                <th className="px-4 py-3">Codice verbale</th><th className="px-4 py-3">Data formazione</th>
                <th className="px-4 py-3">Tema formazione</th><th className="px-4 py-3">Formatore</th>
                <th className="px-4 py-3 text-right">Durata ore</th><th className="px-4 py-3 text-right">Partecipanti</th>
                <th className="px-4 py-3">Prodotto</th><th className="px-4 py-3">Macchina</th>
                <th className="px-4 py-3">Stato</th><th className="px-4 py-3"></th>
              </tr></thead>
              <tbody className="divide-y divide-slate-100">
                {minutes.map(m => (
                  <tr key={m.id} className="hover:bg-slate-50 transition-colors" data-testid={`verbale-row-${m.id}`}>
                    <td className="px-4 py-3 font-mono font-medium text-slate-900">{m.minute_code}</td>
                    <td className="px-4 py-3 text-slate-600">{fmtDate(m.training_date)}</td>
                    <td className="px-4 py-3 text-slate-700 font-medium max-w-xs truncate">{m.topic}</td>
                    <td className="px-4 py-3 text-slate-600">{m.trainer}</td>
                    <td className="px-4 py-3 text-right text-slate-700">{m.duration_hours}</td>
                    <td className="px-4 py-3 text-right text-slate-700">{m.participant_count || 0}</td>
                    <td className="px-4 py-3 text-slate-600 text-xs">{m.product_code ? `${m.product_code}` : '-'}</td>
                    <td className="px-4 py-3 text-slate-600 text-xs">{m.machine_code || '-'}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-block text-xs font-medium px-2 py-0.5 rounded-full ${STATUS_MAP[m.status]?.cls || 'bg-slate-100 text-slate-600'}`}>
                        {STATUS_MAP[m.status]?.label || m.status}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1 justify-end">
                        <Link to={`/training-minutes/${m.id}`} className="p-1 text-slate-400 hover:text-blue-600 rounded" data-testid={`open-verbale-${m.id}`} title="Apri scheda"><ExternalLink className="w-4 h-4" /></Link>
                        {user?.role === 'admin' && <button onClick={() => handleDelete(m.id)} className="p-1 text-slate-400 hover:text-red-600 rounded" data-testid={`delete-verbale-${m.id}`} title="Elimina"><Trash2 className="w-4 h-4" /></button>}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="verbale-create-modal">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-3xl">
            <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200">
              <h2 className="text-lg font-bold text-slate-900">Nuovo verbale formazione</h2>
              <button onClick={() => setShowModal(false)} className="p-1.5 hover:bg-slate-100 rounded-lg"><X className="w-5 h-5 text-slate-500" /></button>
            </div>
            <form onSubmit={handleSubmit} className="p-4 space-y-2">
              <div className="grid grid-cols-4 gap-3">
                <div className="col-span-2"><label className="block text-xs font-medium text-slate-700 mb-1">Tema formazione *</label>
                  <input type="text" value={form.topic} onChange={e => setForm(f => ({ ...f, topic: e.target.value }))} className="w-full px-3 py-1.5 text-sm border border-slate-200 rounded-lg" required data-testid="form-topic" /></div>
                <div><label className="block text-xs font-medium text-slate-700 mb-1">Formatore *</label>
                  <input type="text" value={form.trainer} onChange={e => setForm(f => ({ ...f, trainer: e.target.value }))} className="w-full px-3 py-1.5 text-sm border border-slate-200 rounded-lg" required data-testid="form-trainer" /></div>
                <div><label className="block text-xs font-medium text-slate-700 mb-1">Stato</label>
                  <select value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))} className="w-full px-3 py-1.5 text-sm border border-slate-200 rounded-lg" data-testid="form-status">
                    {Object.entries(STATUS_MAP).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                  </select></div>
              </div>
              <div className="grid grid-cols-5 gap-3">
                <div><label className="block text-xs font-medium text-slate-700 mb-1">Data *</label>
                  <input type="date" value={form.training_date} onChange={e => setForm(f => ({ ...f, training_date: e.target.value }))} className="w-full px-3 py-1.5 text-sm border border-slate-200 rounded-lg" required data-testid="form-date" /></div>
                <div><label className="block text-xs font-medium text-slate-700 mb-1">Ora inizio</label>
                  <input type="time" value={form.start_time} onChange={e => { const v = e.target.value; setForm(f => { const dur = calcDuration(v, f.end_time); return { ...f, start_time: v, ...(dur ? { duration_hours: dur } : {}) }; }); }} className="w-full px-3 py-1.5 text-sm border border-slate-200 rounded-lg" data-testid="form-start-time" /></div>
                <div><label className="block text-xs font-medium text-slate-700 mb-1">Ora fine</label>
                  <input type="time" value={form.end_time} onChange={e => { const v = e.target.value; setForm(f => { const dur = calcDuration(f.start_time, v); return { ...f, end_time: v, ...(dur ? { duration_hours: dur } : {}) }; }); }} className="w-full px-3 py-1.5 text-sm border border-slate-200 rounded-lg" data-testid="form-end-time" /></div>
                <div><label className="block text-xs font-medium text-slate-700 mb-1">Durata ore *</label>
                  <input type="number" step="0.5" min="0.5" value={form.duration_hours} onChange={e => setForm(f => ({ ...f, duration_hours: e.target.value }))} className="w-full px-3 py-1.5 text-sm border border-slate-200 rounded-lg" required data-testid="form-duration" /></div>
                <div><label className="block text-xs font-medium text-slate-700 mb-1">Luogo</label>
                  <input type="text" value={form.location} onChange={e => setForm(f => ({ ...f, location: e.target.value }))} className="w-full px-3 py-1.5 text-sm border border-slate-200 rounded-lg" data-testid="form-location" /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="block text-xs font-medium text-slate-700 mb-1">Prodotto</label>
                  <select value={form.product_id} onChange={e => setForm(f => ({ ...f, product_id: e.target.value, revision_id: '' }))} className="w-full px-3 py-1.5 text-sm border border-slate-200 rounded-lg" data-testid="form-product">
                    <option value="">Nessuno</option>
                    {products.map(p => <option key={p.id} value={p.id}>{p.code} - {p.name}</option>)}
                  </select></div>
                <div><label className="block text-xs font-medium text-slate-700 mb-1">Macchina</label>
                  <select value={form.machine_id} onChange={e => setForm(f => ({ ...f, machine_id: e.target.value }))} className="w-full px-3 py-1.5 text-sm border border-slate-200 rounded-lg" data-testid="form-machine">
                    <option value="">Nessuna</option>
                    {machines.map(m => <option key={m.id} value={m.id}>{m.machine_code} - {m.machine_name}</option>)}
                  </select></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1">Partecipanti ({selectedEmployees.length})</label>
                  <div className="border border-slate-200 rounded-lg max-h-24 overflow-y-auto p-1.5 space-y-0.5" data-testid="form-participants">
                    {employees.filter(e => e.status === 'attivo').map(emp => (
                      <label key={emp.id} className="flex items-center gap-2 px-2 py-0.5 rounded hover:bg-slate-50 cursor-pointer text-sm">
                        <input type="checkbox" checked={selectedEmployees.includes(emp.id)} onChange={() => setSelectedEmployees(s => s.includes(emp.id) ? s.filter(id => id !== emp.id) : [...s, emp.id])} className="rounded border-slate-300" />
                        <span className="font-mono text-xs text-slate-500">{emp.employee_code}</span>
                        <span className="truncate">{emp.last_name} {emp.first_name}</span>
                      </label>
                    ))}
                  </div>
                </div>
                <div><label className="block text-xs font-medium text-slate-700 mb-1">Descrizione</label>
                  <textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} rows={3} className="w-full px-3 py-1.5 text-sm border border-slate-200 rounded-lg" data-testid="form-description" /></div>
              </div>
              <div className="flex justify-end gap-2 pt-2 border-t border-slate-200">
                <Button type="button" variant="outline" size="sm" onClick={() => setShowModal(false)}>Annulla</Button>
                <Button type="submit" size="sm" data-testid="verbale-save-btn">Crea verbale</Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
